import { useState } from 'react';
import axios from 'axios';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * RequestMoreTimeModal — a frosted panel the beta tester opens from
 * the usage meter when they want extra Auto-Conversation minutes.
 * Three fixed block sizes (15 / 30 / 60) plus an optional note. The
 * backend emails the admin on submit; the admin resolves from the
 * Oracle Portal and the user gets notified by email.
 */
const OPTIONS = [
  { minutes: 15, label: '15 minutes', hint: 'A single reflection' },
  { minutes: 30, label: '30 minutes', hint: 'Two sessions, roughly' },
  { minutes: 60, label: '60 minutes', hint: 'The rest of the week' },
];

const RequestMoreTimeModal = ({ open, onClose, onSuccess }) => {
  const [selected, setSelected] = useState(30);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!open) return null;

  const submit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem('sage_token');
      const { data } = await axios.post(
        `${API}/usage/request-more`,
        { minutes: selected, note: note.trim() || null },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      onSuccess?.(data?.status || 'created');
      setNote('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not send your request';
      toast.error(typeof msg === 'string' ? msg : 'Could not send your request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      data-testid="request-more-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Panel — stops click propagation so a tap outside closes but
          taps inside don't. */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-stone-950 border border-white/10 rounded-sm p-7 shadow-2xl"
      >
        <div className="flex items-start justify-between mb-5">
          <div>
            <h3
              className="text-xl font-light text-white mb-1"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}
            >
              Request more time
            </h3>
            <p className="text-xs text-zinc-400">
              Pick a block and we'll review it shortly.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-zinc-500 hover:text-white transition-colors"
            data-testid="close-request-modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2" role="radiogroup" aria-label="Minute block">
          {OPTIONS.map(({ minutes, label, hint }) => (
            <button
              key={minutes}
              type="button"
              role="radio"
              aria-checked={selected === minutes}
              onClick={() => setSelected(minutes)}
              data-testid={`request-option-${minutes}`}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-sm border transition-colors text-left ${
                selected === minutes
                  ? 'bg-amber-500/15 border-amber-400/40 text-amber-100'
                  : 'bg-white/[0.02] border-white/10 text-zinc-300 hover:border-white/25'
              }`}
            >
              <span className="font-medium">{label}</span>
              <span className="text-xs text-zinc-500">{hint}</span>
            </button>
          ))}
        </div>

        <div className="mt-5">
          <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
            Note (optional)
          </label>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            maxLength={400}
            placeholder="Anything you'd like us to know?"
            data-testid="request-note-input"
            className="w-full bg-white/[0.02] border border-white/10 rounded-sm px-3 py-2 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:border-white/30 transition-colors"
          />
        </div>

        <div className="mt-6 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-xs font-mono uppercase tracking-wider text-zinc-400 hover:text-white px-3 py-2 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting}
            data-testid="submit-request-more"
            className="frost-button rounded-sm px-5 py-2.5 text-sm disabled:opacity-60"
          >
            {submitting ? (
              <span className="inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                Sending…
              </span>
            ) : (
              'Send request'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RequestMoreTimeModal;
